import React, { memo, useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';

import { OrganizationOpportunityCard } from 'components/ComponentV2/OrganizationOpportunityCard';
import Link from 'components/Link';
import Icon from 'components/Icon';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useOpportunity } from 'lib/providers/OpportunityProvider';

import styles from './SimilarOpportunities.module.scss';

type SimilarOpportunitiesProps = {
  className?: string;
};

export const SimilarOpportunities: React.FC<SimilarOpportunitiesProps> = memo(({ className }: SimilarOpportunitiesProps) => {
  const [t] = useTranslation('opportunity');
  const { similarOpportunities, opportunity, isSearchPage } = useOpportunity();
  const { touchpointable_type } = opportunity;

  const opportunitySearchPage = useMemo(() => {
    switch (touchpointable_type) {
      case TouchpointType.Internship:
        return PAGE_ROUTES.INTERNSHIPS_SEARCH;
      case TouchpointType.Event:
        return PAGE_ROUTES.EVENTS_SEARCH;
      default:
        return PAGE_ROUTES.SEARCH_RESULTS;
    }
  }, [touchpointable_type]);

  if (!similarOpportunities.length) {
    return null;
  }

  return (
    <div className={cx(styles.container, className)}>
      <header className={styles.header}>
        <h3 className={styles.title}>{t('similar.opportunities')}</h3>
        {!isSearchPage && (
          <Link href={opportunitySearchPage} className={styles.link}>
            <span>{t('similar.explore-more')}</span>
            <Icon iconName="icon_arrow-right-2" size="small" />
          </Link>
        )}
      </header>
      <div className={styles['opportunities-container']}>
        {similarOpportunities.map((opportunity) => (
          <OrganizationOpportunityCard key={opportunity.id} opportunity={opportunity} />
        ))}
      </div>
    </div>
  );
});

SimilarOpportunities.displayName = 'SimilarOpportunities';
